import { RemoveRedEyeTwoTone, SearchTwoTone } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Modal,
  TextField,
  Tooltip
} from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { useAppSelector } from '../../../../redux/root';
import {
  AlQuran,
  Azkaar,
  Countable,
  Khassida,
  UserInfos
} from '../../../../types';
import { deleteCountable } from '../../../../utils/api/user.api';
import { modalStyles } from '../../../../utils/constants';
import CloseModalIcon from '../../../common/Common';
import Table, {
  DeleteButton,
  HeadCell,
  UserFormatter
} from '../../../common/Table';

type ItemCount<T> = {
  item: T;
  count: number;
  counts: Countable[];
  uid: string;
};

export function useItemsCount<T extends Khassida>(
  data: Countable[],
  allItems: T[]
) {
  const [items, setItems] = React.useState<ItemCount<T>[]>([]);
  React.useEffect(() => {
    const obj: { [k: string]: ItemCount<T> } = {};
    for (let u of allItems) {
      obj[u.uid] = {
        item: u,
        count: 0,
        counts: [],
        uid: u.uid
      };
    }

    for (let i of data) {
      if (obj[i.itemId]) {
        obj[i.itemId].count += i.count;
        obj[i.itemId].counts.push(i);
      }
    }
    setItems(
      Object.values(obj)
        .filter((i) => i.count > 0)
        .sort((a, b) => b.count - a.count)
    );
  }, [data, allItems.length]);

  return items;
}

type KhassidaModalProps = {
  show: boolean;
  onHide: () => void;
  data: Countable[];
};

const KhassidaModal = ({ show, onHide, data }: KhassidaModalProps) => {
  const khassida = useAppSelector((s) => s.khassida);
  const filtred = React.useMemo(() => {
    return data.filter((d) => d.khassida);
  }, [data]);

  const items = useItemsCount(filtred, khassida);

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon {...{ onHide }} />
        <ItemCountTable items={items} title="Khassida" />
      </Box>
    </Modal>
  );
};

export default KhassidaModal;

export function ItemCountTable<T extends Khassida>({
  items,
  title = 'Khassida'
}: {
  items: ItemCount<T>[];
  title?: string;
}) {
  const headCells: HeadCell<ItemCount<T>>[] = [
    {
      id: 'item',
      label: title,
      formatter: ({ item }) => item.title
    },
    {
      id: 'count',
      label: 'Lim bi'
    },
    {
      id: 'counts',
      label: 'Action',
      formatter: (item) => (
        <ShowUsersCountButton<Khassida> title={title} item={item} />
      )
    }
  ];

  return <Table headCells={headCells} data={items} />;
}

export function ShowUsersCountButton<T extends Khassida>({
  item,
  title
}: {
  item: ItemCount<T>;
  title: string;
}) {
  const [show, setShow] = React.useState(false);
  return (
    <>
      {show && (
        <LimtuMembreModal
          item={item}
          onHide={() => setShow(false)}
          title={title}
          show={show}
        />
      )}
      <Tooltip title={'Voir les détails'} arrow placement="top-start">
        <Button
          onClick={() => setShow(true)}
          variant="text"
          startIcon={<RemoveRedEyeTwoTone />}
        />
      </Tooltip>
    </>
  );
}

type LimtuMembreModalProps<T extends Khassida> = {
  show: boolean;
  onHide: () => void;
  item: ItemCount<T>;
  title: string;
};

export type CountableMemberWithCount = Countable & {
  item?: Khassida | Azkaar | AlQuran;
};

type MemberWithCount = {
  user: UserInfos;
  count: number;
  counts: CountableMemberWithCount[];
  uid: string;
};

export function LimtuMembreModal<T extends Khassida>({
  show,
  onHide,
  item,
  title
}: LimtuMembreModalProps<T>) {
  const [search, setSearch] = React.useState('');
  const finalTitle = `${title} > ${item.item.title}`;
  const users = useAppSelector((s) => s.admins);

  const data = React.useMemo(() => {
    const finalData: MemberWithCount[] = [];
    const usersMap = new Map(users.map((m) => [m.uid, m]));
    for (let count of item.counts) {
      const finded = usersMap.get(count.user);
      if (finded) {
        const index = finalData.findIndex((m) => m.user.uid === count.user);
        if (index === -1) {
          finalData.push({
            user: finded,
            count: count.count,
            counts: [count],
            uid: finded.uid
          });
        } else {
          finalData[index].count += count.count;
          finalData[index].counts.push(count);
        }
      }
    }
    return finalData;
  }, [users.length, item.counts]);
  const [filtred, setFiltred] = React.useState(data);
  React.useEffect(() => {
    const s = search.toLowerCase().trim();
    if (s.length > 1) {
      setFiltred(
        data.filter(({ user: i }) =>
          `${i.firstName} ${i.lastName} ${i.email} ${i.phone} ${i.matricule}`
            .toLowerCase()
            .includes(s)
        )
      );
    } else {
      setFiltred(data);
    }
  }, [data, search]);
  const headCells: HeadCell<MemberWithCount>[] = [
    {
      id: 'user',
      label: 'Membre',
      formatter: ({ user: { avatar, firstName, lastName, matricule } }) => {
        return (
          <UserFormatter
            avatar={avatar}
            matricule={matricule}
            name={`${firstName} ${lastName}`}
          />
        );
      }
    },
    {
      id: 'count',
      label: 'Limtu'
    },
    {
      id: 'counts',
      label: 'Action',
      formatter: (item) => {
        return <CountsButton item={item} title={finalTitle} />;
      }
    }
  ];

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon title={finalTitle} {...{ onHide }} />
        <Grid container sx={{ mb: 2 }}>
          <Grid item sm={12} md={6}>
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchTwoTone />
                  </InputAdornment>
                )
              }}
              placeholder={'Rechercher...'}
              fullWidth
              label={'Rechercher...'}
            />
          </Grid>
        </Grid>
        <Table headCells={headCells} data={filtred} />
      </Box>
    </Modal>
  );
}

export function CountsButton({
  item,
  title
}: {
  item: MemberWithCount;
  title: string;
}) {
  const [show, setShow] = React.useState(false);
  return (
    <>
      {show && (
        <LimtuModal
          item={item}
          onHide={() => setShow(false)}
          show={show}
          title={title}
        />
      )}
      <Tooltip title={'Voir les détails'} arrow placement="top-start">
        <Button
          onClick={() => setShow(true)}
          variant="text"
          startIcon={<RemoveRedEyeTwoTone />}
        />
      </Tooltip>
    </>
  );
}

type LimtuModalProps = {
  show: boolean;
  onHide: () => void;
  item: MemberWithCount;
  title: string;
};

export function LimtuModal({ show, onHide, item, title }: LimtuModalProps) {
  const headCells: HeadCell<CountableMemberWithCount>[] = [
    {
      id: 'date',
      label: 'Date',
      formatter: ({ date }) => moment(date.date).format('DD/MM/YYYY HH:mm')
    },
    {
      id: 'count',
      label: 'Limtu'
    },
    {
      id: 'itemId',
      label: 'Action',
      formatter: (item) => {
        return (
          <DeleteButton
            id={item.uid}
            error="Une erreur est survenue"
            success="Données supprimées avec succès"
            deleteFunction={async () => {
              await deleteCountable(item.uid);
              onHide();
              return true;
            }}
          />
        );
      }
    }
  ];

  if (item.counts[0]?.item) {
    headCells.splice(0, 0, {
      id: 'salatou',
      label: 'Titre',
      formatter: (item) => item.item?.title ?? '-'
    });
  }

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon
          title={`${title} > ${item.user.firstName} ${item.user.lastName}`}
          {...{ onHide }}
        />
        <Table headCells={headCells} data={item.counts} />
      </Box>
    </Modal>
  );
}
